// A line diff for two texts, the way a reviewer reads two plan versions side
// by side. Plain longest-common-subsequence; a plan is a few hundred lines
// and this runs in the browser once per click, so nothing cleverer is
// needed and no dependency is worth it.

export type DiffLine = {
  kind: "same" | "added" | "removed";
  text: string;
};

export function diffLines(a: string, b: string): DiffLine[] {
  const x = a.split("\n");
  const y = b.split("\n");
  const n = x.length;
  const m = y.length;
  const w = m + 1;
  // lcs[i*w+j] = length of the LCS of x[i:] and y[j:], in one flat table.
  const lcs = new Int32Array((n + 1) * w);
  const at = (i: number, j: number) => lcs[i * w + j] ?? 0;
  const line = (arr: string[], i: number) => arr[i] ?? "";
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i * w + j] =
        line(x, i) === line(y, j) ? at(i + 1, j + 1) + 1 : Math.max(at(i + 1, j), at(i, j + 1));
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (line(x, i) === line(y, j)) {
      out.push({ kind: "same", text: line(x, i) });
      i++;
      j++;
    } else if (at(i + 1, j) >= at(i, j + 1)) {
      out.push({ kind: "removed", text: line(x, i) });
      i++;
    } else {
      out.push({ kind: "added", text: line(y, j) });
      j++;
    }
  }
  for (; i < n; i++) out.push({ kind: "removed", text: line(x, i) });
  for (; j < m; j++) out.push({ kind: "added", text: line(y, j) });
  return out;
}

/** The diff with unchanged stretches folded to a few lines of context. */
export function foldDiff(lines: DiffLine[], context = 3): (DiffLine | { kind: "fold"; count: number })[] {
  const out: (DiffLine | { kind: "fold"; count: number })[] = [];
  let run: DiffLine[] = [];
  const flush = (last: boolean) => {
    if (run.length <= context * 2 + 1 || (out.length === 0 && run.length <= context)) {
      out.push(...run);
    } else if (out.length === 0) {
      out.push({ kind: "fold", count: run.length - context }, ...run.slice(run.length - context));
    } else if (last) {
      out.push(...run.slice(0, context), { kind: "fold", count: run.length - context });
    } else {
      out.push(
        ...run.slice(0, context),
        { kind: "fold", count: run.length - context * 2 },
        ...run.slice(run.length - context),
      );
    }
    run = [];
  };
  for (const l of lines) {
    if (l.kind === "same") {
      run.push(l);
    } else {
      flush(false);
      out.push(l);
    }
  }
  flush(true);
  return out;
}
